import { contactDeleteDialogErrorDescription } from '../../strings.locstring.json';
import { cancelLabel } from 'owa-locstrings/lib/strings/cancellabel.locstring.json';
import { somethingWentWrongLabel } from 'owa-locstrings/lib/strings/somethingwentwronglabel.locstring.json';
import loc from 'owa-localize';
import { deletePersonas as owsDeletePersonas } from '../../dataLayer/ows/persona/deletePersonas';
import { invalidateLpcCachedContacts, unfavoriteDeletedPersonas } from '../utils/hubPersonasUtils';
import {
    createDataActionFactory,
    addNotification,
    Feature,
    createThunk,
    HubPersona,
    PersonType,
    ThunkDispatch,
    getLogSafeId,
    Person,
} from '../../../shared';
import { emptyFolder as owsEmptyFolder } from '../../dataLayer/ows/emptyFolder';
import { deleteContact as capiV3DeleteContact } from '../../dataLayer/peopleGraph/deleteContact';

export interface DeletePersonasInfo {
    deletedHubPersonaIds: string[];
    deletedPersonCount?: number;
    deletedPdlCount?: number;
}

export const deletePersonasCompletedAction = createDataActionFactory<
    DeletePersonasInfo,
    DeletePersonasInfo
>('DeletePersonasCompletedAction', Feature.ContactManagement, action => {
    if (!action.payload.deletedHubPersonaIds) {
        return null;
    }

    return {
        count: action.payload.deletedHubPersonaIds.length,
        pdl: action.payload.deletedPdlCount,
        person: action.payload.deletedPersonCount,
    };
});

const logSafePersonaIds = (personaIds: string[]) => ({
    personaIds: personaIds
        .map(personaId => getLogSafeId(personaId) + '_' + personaId.length)
        .join(','),
});

const getDeletedPersonasStats = (deletedPersonas: HubPersona[]) => {
    let deletedPdlCount = 0;
    let deletedPersonCount = 0;

    deletedPersonas.forEach(deletedHubPerson => {
        if (deletedHubPerson.type === PersonType.Person) {
            deletedPersonCount++;
        } else if (deletedHubPerson.type === PersonType.PrivateDistributionList) {
            deletedPdlCount++;
        }
    });

    return { deletedPdlCount, deletedPersonCount };
};

/**
 * Deletes personas based on a given list of persona ids.
 * The OWS API supports a maximum of 50 persona ids as input.
 */
export const deletePersonas = (deletedPersonas: HubPersona[]) =>
    createThunk('DeletePersonas', (dispatch: ThunkDispatch, getState: () => {}) => {
        const deletedPersonaIds = deletedPersonas
            .map(hubPersona =>
                hubPersona.type !== PersonType.FavoritePrivateDistributionList &&
                hubPersona.type !== PersonType.FavoriteUnifiedGroup
                    ? hubPersona.personaId
                    : undefined
            )
            .filter(id => !!id);
        const deletedHubPersonaIds = deletedPersonas.map(hubPersona => hubPersona.hubPersonaId);

        owsDeletePersonas(deletedPersonaIds, logSafePersonaIds(deletedPersonaIds)).then(
            (wasSuccess: boolean) => {
                deletePersonaSucceeded(
                    wasSuccess,
                    dispatch,
                    deletedHubPersonaIds,
                    deletedPersonas,
                    getState
                );
            },
            () => {
                deletedPersonaFailed(dispatch, deletedHubPersonaIds);
            }
        );
    });

/**
 * Deletes all personas in a folder based on a given folder id.
 * There is no limit on the number of personas which can be deleted.
 */
export const emptyFolder = (folderId: string, deletedPersonas: HubPersona[]) =>
    createThunk('EmptyFolderThunk', (dispatch: ThunkDispatch, getState: () => {}) => {
        const deletedHubPersonaIds = deletedPersonas.map(hubPersona => hubPersona.hubPersonaId);

        owsEmptyFolder(folderId, deletedPersonas.length).then(
            () => {
                invalidateLpcCachedContacts(dispatch, deletedPersonas);
                unfavoriteDeletedPersonas(dispatch, getState, deletedPersonas);

                // The postDelete operations rely on the personas still being in the store,
                // We are dispatching the deleteComplete action now.
                dispatch(
                    deletePersonasCompletedAction.success({
                        deletedHubPersonaIds,
                        ...getDeletedPersonasStats(deletedPersonas),
                    })
                );
            },
            () => {
                // Emptying a folder can take a long time for folders containing lots of contacts. If the operation times out,
                // the deletion of contacts continues in the backend, and we want to invalidate the cache.
                invalidateLpcCachedContacts(dispatch, deletedPersonas);
                dispatch(deletePersonasCompletedAction.error({ deletedHubPersonaIds }));
            }
        );
    });

// CAPIv3 Delete API currently supports only one contact deletion
export const deleteContact = (deletedPersonas: Person[]) =>
    createThunk('DeleteContact', (dispatch: ThunkDispatch, getState: () => {}) => {
        const deletedHubPersonaIds = deletedPersonas.map(hubPersona => hubPersona.hubPersonaId);

        capiV3DeleteContact(deletedPersonas[0].info.contactId).then(
            (response: Response) => {
                deletePersonaSucceeded(
                    response.ok,
                    dispatch,
                    deletedHubPersonaIds,
                    deletedPersonas,
                    getState
                );
            },
            () => {
                deletedPersonaFailed(dispatch, deletedHubPersonaIds);
            }
        );
    });

const deletePersonaSucceeded = (
    wasSuccess: boolean,
    dispatch: ThunkDispatch,
    deletedHubPersonaIds: string[],
    deletedPersonas: HubPersona[],
    getState: () => {}
) => {
    if (!wasSuccess) {
        deletedPersonaFailed(dispatch, deletedHubPersonaIds);
        return;
    }

    invalidateLpcCachedContacts(dispatch, deletedPersonas);
    unfavoriteDeletedPersonas(dispatch, getState, deletedPersonas);

    // The postDelete operations rely on the personas still being in the store,
    // We are dispatching the deleteComplete action now.
    dispatch(
        deletePersonasCompletedAction.success({
            deletedHubPersonaIds,
            ...getDeletedPersonasStats(deletedPersonas),
        })
    );
};

const deletedPersonaFailed = (dispatch: ThunkDispatch, deletedHubPersonaIds: string[]) => {
    dispatch(deletePersonasCompletedAction.error({ deletedHubPersonaIds }));

    addNotification(
        loc(somethingWentWrongLabel),
        loc(contactDeleteDialogErrorDescription),
        loc(cancelLabel)
    );
};

export type { DataActionFactory } from '../../../shared';
export type { Action } from '../../../shared';
